import React, { FC } from 'react';
import {
  CurrentNextLoadCalc,
  DATE_TIME_FORMAT_HM,
  getDayjsNow,
  InputCollection,
  LastCalculations,
  LastTransferSubjects,
  LatestComments,
  Main,
  Spinner,
  Stack,
  SuspendedLoadings,
  useGetUserDashboardSettingsQuery,
  UserBookmarks,
  UserCalendar,
  WaitingStatuses,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DashboardContent.translations.json';
import { Helmet } from 'react-helmet-async';
import { Button, Flex } from 'antd';
import * as Icon from 'react-feather';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { path } from '../../paths';
import styled from 'styled-components';
import { SectionBreadcrumbs } from '../../common/SectionBreadcrumbs';
import { DASHBOARD_ROUTES } from './Dashboard';
import { keepPreviousData } from '@tanstack/react-query';

const { addTranslations, useTranslation } = i18nNsFactory('dashboard-content');
addTranslations(translations);

export const DashboardContent: FC = () => {
  const { t } = useTranslation();
  const navigateToSettings = useNavigateTo(path.dashboardSettings);
  const { data: settings, isLoading } = useGetUserDashboardSettingsQuery({
    placeholderData: keepPreviousData,
  });

  if (isLoading) {
    return <Spinner />;
  }

  if (!settings) {
    return null;
  }

  const showLastCalc =
    settings.showLastCalcInput ||
    settings.showLastCalcOutput ||
    settings.showLastCalcReport;
  const showLatestComments =
    settings.showCommentSample ||
    settings.showCommentOutput ||
    settings.showCommentSubject;

  return (
    <Main>
      <Main.Col>
        <Main.Header>
          <DashboardHeaderStack>
            <Flex gap="small" align="center" justify="space-between">
              <SectionBreadcrumbs data={t('title')} routes={DASHBOARD_ROUTES} />
              <Button
                id="dashboard-settings"
                onClick={() => navigateToSettings()}
              >
                <Icon.Settings />
                <Flex gap="small" align="center">
                  {t('button.settings')}
                </Flex>
              </Button>
            </Flex>
          </DashboardHeaderStack>
          <Helmet>
            <title>{t('title')}</title>
          </Helmet>
        </Main.Header>
        <Main.Content id="dashboard-content">
          <Flex gap="small" style={{ height: '100%' }}>
            <Flex gap="small" vertical flex={0.5} style={{ height: '100%' }}>
              {showLastCalc && <LastCalculations />}
              {settings.showLastTransferSubjects && <LastTransferSubjects />}
              {settings.showWaitingStatuses && <WaitingStatuses />}
              {settings.showSuspendedLoadings && <SuspendedLoadings />}
            </Flex>
            <Flex gap="small" vertical flex={1} style={{ height: '100%' }}>
              <Flex
                gap="small"
                flex={0.5}
                style={{ height: '100%', minHeight: '320px' }}
              >
                <UserCalendar />
                {settings.showBookmarks && <UserBookmarks />}
              </Flex>
              <Flex
                gap="small"
                flex={1}
                vertical
                style={{ height: '100%', minHeight: 0 }}
              >
                {settings.showCurrentNextLoadCalc && <CurrentNextLoadCalc />}
                {settings.showInputCollection && <InputCollection />}
                {showLatestComments && <LatestComments />}
              </Flex>
            </Flex>
          </Flex>
        </Main.Content>
        <Main.Footer>
          {t('refreshTime') + ' ' + getDayjsNow().format(DATE_TIME_FORMAT_HM)}
        </Main.Footer>
      </Main.Col>
    </Main>
  );
};

export const DashboardHeaderStack = styled(Stack)`
  position: sticky;
  left: 52%;
  transform: translateX(-50%);
  margin-inline: -999999px;
  width: 100%;
`;
